<script setup lang="ts">
import {
    Captions,
    Clapperboard,
    MousePointerClick,
    Music,
    Play,
    Scissors,
    ZoomIn,
} from '@lucide/vue';
import FeatureAvailabilityBadge from '@/components/marketing/shared/FeatureAvailabilityBadge.vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const capabilities = [
    'Automatic voiceover and a written script from the recording',
    'Smart zoom and cursor tracking so viewers follow the important clicks',
    'Removed pauses, tighter pacing and smooth transitions',
    'Synchronised captions, background music and brand intros',
    'Landscape, portrait and square versions from one recording',
    'An editable timeline, plus edits you can ask for in plain language',
];

const tracks = [
    { label: 'Video', width: 'w-4/5' },
    { label: 'Voiceover', width: 'w-3/5' },
    { label: 'Captions', width: 'w-2/3' },
    { label: 'Music', width: 'w-1/2' },
];

const tools = [
    { icon: Play, label: 'Voiceover' },
    { icon: Captions, label: 'Captions' },
    { icon: ZoomIn, label: 'Smart zoom' },
    { icon: MousePointerClick, label: 'Cursor' },
    { icon: Scissors, label: 'Pacing' },
    { icon: Music, label: 'Music' },
];
</script>

<template>
    <section id="video-studio" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                align="left"
                eyebrow="AI Video Studio"
                title="Turn screen recordings into polished videos."
                description="Upload a recording and let your assistant cut, narrate, caption and reframe it. You still get the final say on the timeline."
            />

            <div class="mt-6 flex justify-start">
                <FeatureAvailabilityBadge status="coming-soon" />
            </div>

            <div
                class="mt-10 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center"
            >
                <div
                    class="bg-card border-border overflow-hidden rounded-2xl border shadow-sm"
                    aria-hidden="true"
                >
                    <div
                        class="bg-muted relative flex aspect-video items-center justify-center"
                    >
                        <Clapperboard
                            class="text-muted-foreground/50 size-10"
                        />
                        <span
                            class="bg-background/85 text-muted-foreground absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[10px]"
                        >
                            Preview
                        </span>
                    </div>

                    <div class="grid gap-2 border-t p-4">
                        <div
                            v-for="track in tracks"
                            :key="track.label"
                            class="flex items-center gap-3"
                        >
                            <span
                                class="text-muted-foreground w-20 shrink-0 text-[11px]"
                            >
                                {{ track.label }}
                            </span>
                            <span class="bg-muted h-5 flex-1 rounded-md">
                                <span
                                    :class="[
                                        'bg-secondary/30 block h-full rounded-md',
                                        track.width,
                                    ]"
                                />
                            </span>
                        </div>
                    </div>

                    <div
                        class="border-border flex flex-wrap gap-2 border-t p-4"
                    >
                        <span
                            v-for="tool in tools"
                            :key="tool.label"
                            class="border-border text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px]"
                        >
                            <component :is="tool.icon" class="size-3" />
                            {{ tool.label }}
                        </span>
                    </div>
                </div>

                <ul class="grid gap-3">
                    <li
                        v-for="item in capabilities"
                        :key="item"
                        class="flex items-start gap-3 text-sm sm:text-base"
                    >
                        <Play
                            class="text-primary mt-0.5 size-4 shrink-0"
                            aria-hidden="true"
                        />
                        {{ item }}
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>
